<script setup>
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  title: { type: String, default: '' },
  prefixIcon: { type: String, default: '' },
  prefixIconClass: { type: String, default: 'text-n-blue-11' },
  density: {
    type: String,
    default: 'cozy',
    validator: value => ['cozy', 'tight', 'flush'].includes(value),
  },
  href: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  active: { type: Boolean, default: false },
  interactive: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  ariaLabel: { type: String, default: '' },
  expandable: { type: Boolean, default: false },
  startExpanded: { type: Boolean, default: true },
  expandLabel: { type: String, default: 'Expandir conteúdo' },
  collapseLabel: { type: String, default: 'Recolher conteúdo' },
});

const emit = defineEmits(['click', 'toggle']);
const isExpanded = ref(props.startExpanded);

const isActionable = computed(() =>
  Boolean(props.href || props.interactive || props.selectable)
);

const rootTag = computed(() => {
  if (props.href && !props.disabled) return 'a';
  if (isActionable.value) return 'div';
  return 'section';
});

const rootClasses = computed(() => [
  'flex min-w-0 flex-col transition-colors',
  isActionable.value &&
    'overflow-hidden rounded-xl border border-n-weak bg-n-solid-2',
  (props.selected || props.active) && 'border-n-brand ring-2 ring-n-brand/20',
  {
    'cursor-pointer hover:border-n-strong':
      isActionable.value && !props.disabled,
    'cursor-not-allowed opacity-50': props.disabled,
  },
]);

const contentClasses = computed(() => {
  if (!isActionable.value) return 'pt-4';

  return {
    'p-5': props.density === 'cozy',
    'px-5 py-4': props.density === 'tight',
    'p-0': props.density === 'flush',
  };
});

const handleClick = event => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }

  emit('click', event);
};

const handleKeydown = event => {
  if (!isActionable.value || props.disabled) return;
  if (!['Enter', ' '].includes(event.key)) return;

  event.preventDefault();
  emit('click', event);
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
  emit('toggle', isExpanded.value);
};
</script>

<template>
  <component
    :is="rootTag"
    :href="rootTag === 'a' ? href : undefined"
    :role="rootTag === 'div' && isActionable ? 'button' : undefined"
    :tabindex="rootTag === 'div' && isActionable && !disabled ? 0 : undefined"
    :aria-label="ariaLabel || undefined"
    :aria-pressed="selectable ? selected : undefined"
    :aria-current="active ? 'true' : undefined"
    :aria-disabled="disabled && isActionable ? 'true' : undefined"
    :class="rootClasses"
    data-testid="agent-section"
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <header
      v-if="
        $slots.header ||
        title ||
        prefixIcon ||
        expandable ||
        $slots.titleAction ||
        $slots.suffix
      "
      class="flex items-center justify-between gap-3"
      :class="
        isActionable
          ? 'border-b border-n-weak px-5 py-4'
          : 'border-b border-n-weak pb-3'
      "
    >
      <slot name="header">
        <div class="flex min-w-0 items-center gap-2">
          <span
            v-if="prefixIcon"
            class="size-4 shrink-0"
            :class="[prefixIcon, prefixIconClass]"
            aria-hidden="true"
          />
          <h2
            v-if="title"
            class="truncate text-base font-medium text-n-slate-12"
          >
            {{ title }}
          </h2>
        </div>
      </slot>

      <div
        v-if="$slots.titleAction || $slots.suffix || expandable"
        class="flex items-center gap-2"
      >
        <slot name="titleAction">
          <slot name="suffix" />
        </slot>
        <Button
          v-if="expandable"
          icon="i-lucide-chevron-down"
          :aria-label="isExpanded ? collapseLabel : expandLabel"
          :aria-expanded="isExpanded"
          :class="{ 'rotate-180': isExpanded }"
          size="xs"
          color="slate"
          variant="ghost"
          type="button"
          @click.stop="toggleExpanded"
        />
      </div>
    </header>

    <div
      v-show="!expandable || isExpanded"
      :class="contentClasses"
      data-testid="agent-section-content"
    >
      <slot />
    </div>

    <footer
      v-if="$slots.footer"
      class="border-t border-n-weak"
      :class="isActionable ? 'bg-n-solid-1 px-5 py-4' : 'mt-4 pt-4'"
    >
      <slot name="footer" />
    </footer>
  </component>
</template>
